import React, { useMemo } from "react";
import {
  Alert, Button, Col, InputNumber, Modal, Row, Select, Space, Table, Tag,
} from "antd";

import { solveSurvey } from "./survey";

// 由距離換算座標。
//
// 現地沒有 RTK 或全站儀時，捲尺量出的是點與點之間的距離。這個視窗讓人把
// 距離一筆一筆填進來，即時換算出每個 GRP 的局部 X/Y，再一鍵填回 GRP 表格。
//
// 量測資料存在 params.survey，跟著參數一起儲存 —— 現場量一次很費工，關掉
// 視窗或之後要補量一段時，不該要人重新輸入。

const fin = (v) => typeof v === "number" && Number.isFinite(v);

// 平差後差值超過這個值，多半是那一段量錯或抄錯
const RESIDUAL_WARN = 0.05;

export const EMPTY_SURVEY = {
  origin: null, axis: null, x0: 0, y0: 0, distances: [],
};

/**
 * 刪除第 removed 個 GRP 後，修正量測資料裡的點編號。
 * 涉及被刪點的量測一併移除，後面的編號往前遞補。
 */
export function remapSurvey(survey, removed) {
  const s = survey || EMPTY_SURVEY;
  const shift = (k) => (k === null || k === undefined ? k
    : k === removed ? null : k > removed ? k - 1 : k);
  return {
    ...s,
    origin: shift(s.origin),
    axis: shift(s.axis),
    distances: (s.distances || [])
      .filter((m) => m.i !== removed && m.j !== removed)
      .map((m) => ({ ...m, i: shift(m.i), j: shift(m.j) })),
  };
}

export default function SurveyModal({
  open, onClose, grps, pixels, survey, onChange, onApply,
}) {
  const s = { ...EMPTY_SURVEY, ...(survey || {}) };
  const rows = s.distances || [];
  const options = grps.map((_, i) => ({ value: i, label: `#${i + 1}` }));

  const set = (patch) => onChange({ ...s, ...patch });
  const setRow = (k, patch) =>
    set({ distances: rows.map((r, n) => (n === k ? { ...r, ...patch } : r)) });
  const addRow = (i = null, j = null) =>
    set({ distances: [...rows, { i, j, d: null }] });

  // 直接從 survey 展開，而不是用上面的 s：s 每次 render 都是新物件，放進
  // 依賴陣列會讓 useMemo 形同虛設
  const result = useMemo(
    () => solveSurvey({ ...EMPTY_SURVEY, ...(survey || {}), pixels }),
    [survey, pixels]);

  // 快速建立：每一點到原點、方向點各一列（現場最常見的量法），已有的不重複
  const quickFill = () => {
    const has = (a, b) => rows.some((r) =>
      (r.i === a && r.j === b) || (r.i === b && r.j === a));
    const added = [];
    const want = [[s.origin, s.axis]];
    grps.forEach((_, k) => {
      if (k === s.origin || k === s.axis) return;
      want.push([k, s.origin], [k, s.axis]);
    });
    want.forEach(([a, b]) => {
      if (!has(a, b) && !added.some((r) => r.i === a && r.j === b)) {
        added.push({ i: a, j: b, d: null });
      }
    });
    if (added.length) set({ distances: [...rows, ...added] });
  };

  const placedCount = Object.keys(result.coords).length;
  const residualOf = (r) => result.residuals.find((x) =>
    (x.i === r.i && x.j === r.j) || (x.i === r.j && x.j === r.i));

  return (
    <Modal title="由距離換算座標" open={open} width={980} onCancel={onClose}
           footer={[
             <Button key="close" onClick={onClose}>關閉</Button>,
             <Button key="apply" type="primary"
                     disabled={!!result.error || placedCount === 0}
                     onClick={() => onApply(result.coords)}>
               套用到 GRP 表格（{placedCount} 點）
             </Button>,
           ]}>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
             message="選一點當原點、另一點定出 +X 方向，其餘每一點量到至少兩個點的水平距離"
             description={
               <ul style={{ margin: 0, paddingLeft: 16 }}>
                 <li>請填<b>水平距離</b>；沿斜坡拉的捲尺讀數要先換算成水平距離</li>
                 <li>每點量到<b>三個以上</b>的點，才能檢查出量錯的那一段</li>
                 <li>左右方向由影像上已指認的點自動判斷，所以建議先在影像上把點標好</li>
                 <li>換算出的是<b>局部座標</b>，不能和 RTK（TWD97）或舊的測量座標混用</li>
               </ul>} />

      <Row gutter={16}>
        <Col span={12}>
          <Row gutter={8} style={{ marginBottom: 8 }}>
            <Col span={6}>
              <div style={{ fontSize: 12, marginBottom: 2 }}>原點</div>
              <Select size="small" style={{ width: "100%" }} options={options}
                      value={s.origin} placeholder="選點"
                      onChange={(value) => set({ origin: value })} />
            </Col>
            <Col span={6}>
              <div style={{ fontSize: 12, marginBottom: 2 }}>+X 方向點</div>
              <Select size="small" style={{ width: "100%" }} options={options}
                      value={s.axis} placeholder="選點"
                      onChange={(value) => set({ axis: value })} />
            </Col>
            <Col span={6}>
              <div style={{ fontSize: 12, marginBottom: 2 }}>原點 X (m)</div>
              <InputNumber size="small" style={{ width: "100%" }} step={1}
                           value={s.x0} onChange={(value) => set({ x0: value ?? 0 })} />
            </Col>
            <Col span={6}>
              <div style={{ fontSize: 12, marginBottom: 2 }}>原點 Y (m)</div>
              <InputNumber size="small" style={{ width: "100%" }} step={1}
                           value={s.y0} onChange={(value) => set({ y0: value ?? 0 })} />
            </Col>
          </Row>

          <Space style={{ marginBottom: 8 }}>
            <Button size="small" onClick={() => addRow()}>新增一筆距離</Button>
            <Button size="small"
                    disabled={!Number.isInteger(s.origin) || !Number.isInteger(s.axis)
                      || s.origin === s.axis}
                    onClick={quickFill}>
              快速建立：每點到原點與方向點
            </Button>
          </Space>

          <Table size="small" rowKey={(_, k) => k} pagination={false}
                 scroll={{ y: 300 }} dataSource={rows}
                 locale={{ emptyText: "尚未輸入距離" }}
                 columns={[
                   { title: "從", width: 72,
                     render: (_, r, k) => (
                       <Select size="small" style={{ width: "100%" }} options={options}
                               value={r.i} onChange={(value) => setRow(k, { i: value })} />) },
                   { title: "到", width: 72,
                     render: (_, r, k) => (
                       <Select size="small" style={{ width: "100%" }} options={options}
                               value={r.j} onChange={(value) => setRow(k, { j: value })} />) },
                   { title: "水平距離 (m)", width: 110,
                     render: (_, r, k) => (
                       <InputNumber size="small" style={{ width: "100%" }} min={0}
                                    step={0.01} value={r.d} placeholder="待量"
                                    onChange={(value) => setRow(k, { d: value })} />) },
                   { title: "差值", width: 64, align: "right",
                     render: (_, r) => {
                       const x = residualOf(r);
                       if (!x) return "—";
                       const bad = Math.abs(x.res) > RESIDUAL_WARN;
                       return (
                         <span style={{ color: bad ? "#d4380d" : undefined,
                                        fontWeight: bad ? 600 : undefined }}>
                           {x.res > 0 ? "+" : ""}{x.res.toFixed(3)}
                         </span>);
                     } },
                   { title: "", width: 44,
                     render: (_, __, k) => (
                       <Button size="small" type="link" danger style={{ padding: 0 }}
                               onClick={() => set({
                                 distances: rows.filter((_, n) => n !== k) })}>
                         刪除
                       </Button>) },
                 ]} />
          <div style={{ fontSize: 11, color: "var(--secondary-text)", marginTop: 4 }}>
            差值 = 換算後兩點的距離 − 填入的距離。超過 {RESIDUAL_WARN * 100} cm
            標紅，多半是那一段量錯或抄錯。
          </div>
        </Col>

        <Col span={12}>
          {result.error ? (
            <Alert type="warning" showIcon message={result.error} />
          ) : (
            <>
              <Space size={6} wrap style={{ marginBottom: 8 }}>
                <Tag color={result.orientation === "image" ? "success" : "warning"}>
                  {result.orientation === "image" ? "方向由影像判斷" : "方向未確認"}
                </Tag>
                {result.rms !== null && (
                  <Tag color={result.rms > RESIDUAL_WARN ? "error" : "default"}>
                    差值均方根 {(result.rms * 100).toFixed(1)} cm
                  </Tag>
                )}
                {result.redundancy !== null && (
                  <Tag color={result.redundancy > 0 ? "default" : "warning"}>
                    多餘量測 {Math.max(0, result.redundancy)} 筆
                  </Tag>
                )}
              </Space>

              <Table size="small" rowKey="k" pagination={false} scroll={{ y: 300 }}
                     dataSource={grps.map((g, k) => ({ k, g }))}
                     columns={[
                       { title: "#", width: 40, render: (_, r) => r.k + 1 },
                       { title: "換算 X", width: 90, align: "right",
                         render: (_, r) => (result.coords[r.k]
                           ? result.coords[r.k].X.toFixed(3) : "—") },
                       { title: "換算 Y", width: 90, align: "right",
                         render: (_, r) => (result.coords[r.k]
                           ? result.coords[r.k].Y.toFixed(3) : "—") },
                       { title: "目前表格值", align: "right",
                         render: (_, r) => (
                           <span style={{ color: "var(--secondary-text)", fontSize: 12 }}>
                             {fin(r.g.X) && fin(r.g.Y)
                               ? `${r.g.X}, ${r.g.Y}` : "待實測"}
                           </span>) },
                     ]} />
            </>
          )}

          {result.warnings.length > 0 && (
            <Alert style={{ marginTop: 8 }} type="warning" showIcon
                   message="注意"
                   description={
                     <ul style={{ margin: 0, paddingLeft: 16 }}>
                       {result.warnings.map((w) => <li key={w}>{w}</li>)}
                     </ul>} />
          )}
        </Col>
      </Row>
    </Modal>
  );
}